import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, ScanLine, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface CameraCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the captured JPEG when the cashier snaps a photo. */
  onCapture: (file: File) => void;
}

/** Gold L-brackets marking the document frame — the scanner reticle corners. */
function FrameCorners() {
  const base = 'absolute h-8 w-8 border-gold-500 drop-shadow-[0_0_6px_rgba(255,215,0,0.5)]';
  return (
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[62%] w-[84%] -translate-x-1/2 -translate-y-1/2">
      <span className={`${base} -left-1 -top-1 rounded-tl-2xl border-l-[3px] border-t-[3px]`} />
      <span className={`${base} -right-1 -top-1 rounded-tr-2xl border-r-[3px] border-t-[3px]`} />
      <span className={`${base} -bottom-1 -left-1 rounded-bl-2xl border-b-[3px] border-l-[3px]`} />
      <span className={`${base} -bottom-1 -right-1 rounded-br-2xl border-b-[3px] border-r-[3px]`} />
    </div>
  );
}

/**
 * Live-camera document capture. Opens the device camera (rear-facing when
 * available), shows a large scanner-style viewfinder with a gold document frame
 * + sweeping scan line, and snaps the current frame to a JPEG File that feeds
 * the same OCR flow as an uploaded photo. Requires a secure context (HTTPS or
 * localhost) — falls back to the upload button on denial / no camera.
 */
export function CameraCaptureDialog({ open, onOpenChange, onCapture }: CameraCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [flash, setFlash] = useState(false);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
    setFlash(false);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Kamera tidak didukung di perangkat ini. Gunakan unggah foto.');
        onOpenChange(false);
        return;
      }
      try {
        // Prefer the rear camera; fall back to any available camera.
        const stream = await navigator.mediaDevices
          .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
          .catch(() => navigator.mediaDevices.getUserMedia({ video: true, audio: false }));
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch {
        toast.error('Tidak bisa mengakses kamera. Izinkan akses atau gunakan unggah foto.');
        onOpenChange(false);
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, onOpenChange]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setFlash(true);
    window.setTimeout(() => setFlash(false), 360);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error('Gagal mengambil foto. Coba lagi.');
          return;
        }
        const file = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        onOpenChange(false);
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="w-[96vw] max-w-4xl gap-0 overflow-hidden border-0 bg-ink-900 p-0 shadow-pop"
      >
        {/* ── Viewfinder ─────────────────────────────────────────────── */}
        <div className="relative aspect-[4/3] max-h-[68vh] w-full overflow-hidden bg-black">
          <video
            ref={videoRef}
            playsInline
            muted
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Scanner reticle — only once the stream is live */}
          {ready && (
            <div className="pointer-events-none absolute inset-0 z-10">
              {/* Spotlight: dim everything outside the document frame */}
              <div className="absolute left-1/2 top-1/2 h-[62%] w-[84%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]">
                {/* Sweeping scan line */}
                <div className="absolute inset-x-0 h-20 bg-gradient-to-b from-transparent via-gold-500/40 to-transparent motion-safe:animate-doc-scan motion-reduce:hidden" />
              </div>
              <FrameCorners />
            </div>
          )}

          {/* Loading state */}
          {!ready && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/85 text-white/80">
              <Loader2 className="h-7 w-7 animate-spin text-gold-500" />
              <p className="text-sm">Menyiapkan kamera…</p>
            </div>
          )}

          {/* Top bar */}
          <div className="absolute inset-x-0 top-0 z-30 flex items-start justify-between gap-3 bg-gradient-to-b from-black/75 via-black/30 to-transparent px-4 pb-8 pt-3.5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-500 ring-1 ring-gold-500/30">
                <ScanLine className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <DialogTitle className="font-display text-base font-semibold text-white">
                  Ambil foto dokumen
                </DialogTitle>
                <DialogDescription className="text-xs text-white/60">
                  Bingkai emas menandai area dokumen.
                </DialogDescription>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Tutup"
              className="shrink-0 rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Shutter flash */}
          {flash && (
            <div className="pointer-events-none absolute inset-0 z-40 bg-white animate-doc-flash" />
          )}
        </div>

        {/* ── Control bar ────────────────────────────────────────────── */}
        <div className="bg-ink-900 px-5 pb-6 pt-4">
          <p className="mb-4 text-center text-sm text-white/70">
            Posisikan seluruh dokumen di dalam bingkai, lalu ketuk untuk memotret.
          </p>
          <div className="relative flex items-center justify-center">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="absolute left-0 border-white/20 bg-transparent text-white hover:border-white/30 hover:bg-white/10 hover:text-white"
            >
              Batal
            </Button>

            {/* Shutter */}
            <button
              type="button"
              onClick={capture}
              disabled={!ready}
              aria-label="Ambil foto"
              className="group relative h-[72px] w-[72px] rounded-full border-[3px] border-gold-500/90 transition disabled:cursor-not-allowed disabled:border-white/25"
            >
              <span className="absolute inset-[6px] flex items-center justify-center rounded-full bg-gold-500 text-ink-900 shadow-[0_0_22px_-2px_rgba(255,215,0,0.75)] transition-transform duration-100 group-active:scale-90 group-disabled:bg-white/25 group-disabled:text-white/50 group-disabled:shadow-none">
                <Camera className="h-6 w-6" />
              </span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
